import type {
  TechnicalMetadata,
  AudioMetadata,
  ProjectFilesMetadata,
} from '@/types/packageSummary';

/**
 * Format duration from seconds to MM:SS
 */
const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Render a boolean value with icons
 */
const BooleanValue = ({ value }: { value: boolean | null }) => {
  if (value === null) return <span className="text-gray-400">—</span>;
  return value ? (
    <span className="text-green-600">✓</span>
  ) : (
    <span className="text-red-600">✗</span>
  );
};

/**
 * Technical metadata column
 */
export const TechnicalColumn = ({ technical }: { technical: TechnicalMetadata }) => {
  // Check if duration info differs significantly (more than 0.01s)
  const hasDurationMismatch =
    technical.expected_duration_seconds !== null &&
    technical.duration !== null &&
    Math.abs(technical.duration - technical.expected_duration_seconds) > 0.01;

  return (
    <div className="space-y-1 text-sm">
      <div className="font-semibold text-gray-700 mb-2">Technical</div>
      <div className="flex justify-between">
        <span className="text-gray-600">Duration:</span>
        <span className="font-medium">{formatDuration(technical.duration)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Resolution:</span>
        <span className="font-medium">{technical.resolution || '—'}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Frame Rate:</span>
        <span className="font-medium">
          {technical.frame_rate !== null ? `${technical.frame_rate} fps` : '—'}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Title Safe:</span>
        <BooleanValue value={technical.title_safe_passed} />
      </div>

      {/* Duration Information Section */}
      {(technical.duration_timecode || technical.expected_duration_timecode) && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="font-semibold text-gray-700 mb-2">Duration Information</div>
          {technical.duration_timecode && (
            <div className="flex justify-between">
              <span className="text-gray-600">Actual:</span>
              <span className={`font-medium ${hasDurationMismatch ? 'text-amber-600' : ''}`}>
                {technical.duration_timecode}
                {hasDurationMismatch && ' ⚠️'}
              </span>
            </div>
          )}
          {technical.expected_duration_timecode && (
            <div className="flex justify-between">
              <span className="text-gray-600">Expected:</span>
              <span className="font-medium text-gray-500 text-xs">
                {technical.expected_duration_timecode}
                <span className="ml-1">(from filename)</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Audio metadata column
 */
export const AudioColumn = ({ audio }: { audio: AudioMetadata }) => {
  return (
    <div className="space-y-1 text-sm">
      <div className="font-semibold text-gray-700 mb-2">Audio</div>
      <div className="flex justify-between">
        <span className="text-gray-600">Embedded Audio:</span>
        <BooleanValue value={audio.embedded_audio} />
      </div>
      {audio.embedded_audio && (
        <div className="flex justify-between pl-4">
          <span className="text-gray-600">Stereo:</span>
          <BooleanValue value={audio.embedded_audio_stereo} />
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-gray-600">MIX:</span>
        <BooleanValue value={audio.mix_present} />
      </div>
      {audio.mix_present && (
        <div className="flex justify-between pl-4">
          <span className="text-gray-600">Stereo:</span>
          <BooleanValue value={audio.mix_stereo} />
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-gray-600">Music:</span>
        <BooleanValue value={audio.music_present} />
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">VO:</span>
        <BooleanValue value={audio.vo_present} />
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">SFX:</span>
        <BooleanValue value={audio.sfx_present} />
      </div>
      {audio.other_elements_present && audio.other_elements && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="text-gray-600 mb-1">Other Elements:</div>
          <div className="text-xs text-gray-500">
            {audio.other_elements.join(', ')}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Project files metadata column
 */
export const ProjectFilesColumn = ({ projectFiles }: { projectFiles: ProjectFilesMetadata }) => {
  return (
    <div className="space-y-1 text-sm">
      <div className="font-semibold text-gray-700 mb-2">Project Files</div>
      <div className="flex justify-between">
        <span className="text-gray-600">After Effects:</span>
        <BooleanValue value={projectFiles.after_effects_present} />
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Premiere Pro:</span>
        <BooleanValue value={projectFiles.premiere_pro_present} />
      </div>
    </div>
  );
};
